import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FieldType, type InvitePrefill, type OnboardingFieldDto } from '@ciimar/shared';
import { api, ApiError } from '../api/client';
import { pickText, useI18n, useLocalizedLabel, useT } from '../lib/i18n';

interface FormState {
  fullName: string;
  email: string;
  phone: string;
  jobTitle: string;
  department: string;
  researchGroup: string;
  startDate: string;
  notes: string;
  website: string; // honeypot — must stay empty
}

const empty: FormState = {
  fullName: '',
  email: '',
  phone: '',
  jobTitle: '',
  department: '',
  researchGroup: '',
  startDate: '',
  notes: '',
  website: '',
};

type Phase = 'validating' | 'invalid' | 'ready' | 'done';

export function IamNew() {
  const t = useT();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const code = params.get('code') ?? '';

  const [phase, setPhase] = useState<Phase>('validating');
  const [invalidReason, setInvalidReason] = useState<string>('');
  const [prefill, setPrefill] = useState<InvitePrefill | null>(null);
  const [form, setForm] = useState<FormState>(empty);
  const [fields, setFields] = useState<OnboardingFieldDto[]>([]);
  const [extra, setExtra] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const formStartedAt = useRef<number>(Date.now());

  useEffect(() => {
    if (!token && !code) {
      setInvalidReason(t('auth.iamnew.needInvite'));
      setPhase('invalid');
      return;
    }
    // The admin-curated extra fields are public and independent of the invite.
    api
      .onboardingFields()
      .then(setFields)
      .catch(() => setFields([]));
    api
      .validateInvite({ token: token || undefined, code: code || undefined })
      .then((p) => {
        setPrefill(p);
        setForm((prev) => ({
          ...prev,
          email: p.targetEmail ?? '',
          researchGroup: p.researchGroup ?? '',
          department: p.department ?? '',
        }));
        formStartedAt.current = Date.now();
        setPhase('ready');
      })
      .catch((err) => {
        setInvalidReason(err instanceof ApiError ? err.message : t('auth.iamnew.inviteInvalid'));
        setPhase('invalid');
      });
  }, [token, code]);

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function updateExtra(key: string, value: string) {
    setExtra((prev) => ({ ...prev, [key]: value }));
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.selfRegister({
        token: token || undefined,
        code: code || undefined,
        fullName: form.fullName,
        email: form.email,
        phone: form.phone || undefined,
        jobTitle: form.jobTitle || undefined,
        department: form.department || undefined,
        researchGroup: form.researchGroup || undefined,
        startDate: form.startDate || undefined,
        notes: form.notes || undefined,
        extraFields: Object.keys(extra).length > 0 ? extra : undefined,
        website: form.website || undefined,
        // Populated by Cloudflare Turnstile when configured (see README).
        captchaToken: readTurnstileToken(),
        formStartedAt: formStartedAt.current,
      });
      setPhase('done');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('auth.iamnew.submitError'));
    } finally {
      setSubmitting(false);
    }
  }

  if (phase === 'validating') {
    return <div className="public-page">{t('auth.iamnew.validating')}</div>;
  }

  if (phase === 'invalid') {
    return (
      <div className="public-page">
        <div className="card">
          <h1>{t('auth.iamnew.unavailableTitle')}</h1>
          <p className="muted">{invalidReason}</p>
        </div>
      </div>
    );
  }

  if (phase === 'done') {
    return (
      <div className="public-page">
        <div className="card success">
          <h1>{t('auth.iamnew.doneTitle')}</h1>
          <p>{t('auth.iamnew.doneBody')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="public-page">
      <form className="card" onSubmit={onSubmit}>
        <h1>{t('auth.iamnew.title')}</h1>
        <p className="muted">
          {prefill?.requestedByName
            ? t('auth.iamnew.invitedBy', { name: prefill.requestedByName })
            : ''}
          {t('auth.iamnew.fillIn')}
        </p>

        {error && <div className="alert error">{error}</div>}

        <label>
          {t('auth.iamnew.fullName')}
          <input
            required
            value={form.fullName}
            onChange={(e) => update('fullName', e.target.value)}
            autoComplete="name"
          />
        </label>
        <label>
          {t('auth.iamnew.email')}
          <input
            required
            type="email"
            value={form.email}
            onChange={(e) => update('email', e.target.value)}
            autoComplete="email"
          />
        </label>
        <div className="row">
          <label>
            {t('auth.iamnew.phone')}
            <input
              value={form.phone}
              onChange={(e) => update('phone', e.target.value)}
              autoComplete="tel"
            />
          </label>
          <label>
            {t('auth.iamnew.startDate')}
            <input
              type="date"
              value={form.startDate}
              onChange={(e) => update('startDate', e.target.value)}
            />
          </label>
        </div>
        <div className="row">
          <label>
            {t('auth.iamnew.jobTitle')}
            <input value={form.jobTitle} onChange={(e) => update('jobTitle', e.target.value)} />
          </label>
          <label>
            {t('auth.iamnew.department')}
            <input value={form.department} onChange={(e) => update('department', e.target.value)} />
          </label>
        </div>
        <label>
          {t('auth.iamnew.researchGroup')}
          <input
            value={form.researchGroup}
            onChange={(e) => update('researchGroup', e.target.value)}
          />
        </label>
        <label>
          {t('auth.iamnew.notes')}
          <textarea rows={3} value={form.notes} onChange={(e) => update('notes', e.target.value)} />
        </label>

        {fields.length > 0 && (
          <div className="onb-extra">
            <h2>{t('auth.iamnew.extraTitle')}</h2>
            {fields.map((field) => (
              <OnbField
                key={field.key}
                field={field}
                value={extra[field.key] ?? ''}
                onChange={(v) => updateExtra(field.key, v)}
              />
            ))}
          </div>
        )}

        {/* Honeypot: hidden from real users; bots that fill it are rejected. */}
        <div className="hp" aria-hidden="true">
          <label>
            Website
            <input
              tabIndex={-1}
              autoComplete="off"
              value={form.website}
              onChange={(e) => update('website', e.target.value)}
            />
          </label>
        </div>

        {/* Cloudflare Turnstile widget mounts here when configured. */}
        <div className="cf-turnstile" data-sitekey="" />

        <button type="submit" disabled={submitting}>
          {submitting ? t('auth.iamnew.submitting') : t('auth.iamnew.submit')}
        </button>
      </form>
    </div>
  );
}

/** Reads the Turnstile response token from the page, if the widget is present. */
function readTurnstileToken(): string | undefined {
  const input = document.querySelector<HTMLInputElement>('input[name="cf-turnstile-response"]');
  return input?.value || undefined;
}

/**
 * Renders one admin-curated onboarding field. The control is chosen from the
 * field type; `required` is inherited from the general field configuration.
 * Reference fields arrive without options and render as free text — the public
 * form never exposes SISTRADE reference values.
 */
function OnbField({
  field,
  value,
  onChange,
}: {
  field: OnboardingFieldDto;
  value: string;
  onChange: (value: string) => void;
}) {
  const { lang } = useI18n();
  const loc = useLocalizedLabel();
  const label = (
    <>
      {pickText(field.label, field.labelEn, lang)}
      {field.required ? ' *' : ''}
    </>
  );

  if (field.type === FieldType.LongText) {
    return (
      <label>
        {label}
        <textarea
          rows={3}
          required={field.required}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      </label>
    );
  }

  if (field.type === FieldType.Boolean) {
    return (
      <label className="onb-check">
        <input
          type="checkbox"
          checked={value === 'true'}
          onChange={(e) => onChange(e.target.checked ? 'true' : 'false')}
        />
        {label}
      </label>
    );
  }

  if (field.type === FieldType.Select) {
    return (
      <label>
        {label}
        <select required={field.required} value={value} onChange={(e) => onChange(e.target.value)}>
          <option value="">—</option>
          {field.options.map((o) => (
            <option key={o.value} value={o.value}>
              {loc(o)}
            </option>
          ))}
        </select>
      </label>
    );
  }

  if (field.type === FieldType.MultiSelect) {
    const selected = new Set(value ? value.split('|') : []);
    const toggle = (optValue: string, on: boolean) => {
      if (on) selected.add(optValue);
      else selected.delete(optValue);
      onChange(Array.from(selected).join('|'));
    };
    return (
      <fieldset className="onb-multi">
        <legend>{label}</legend>
        {field.options.map((o) => (
          <label key={o.value} className="onb-check">
            <input
              type="checkbox"
              checked={selected.has(o.value)}
              onChange={(e) => toggle(o.value, e.target.checked)}
            />
            {loc(o)}
          </label>
        ))}
      </fieldset>
    );
  }

  return (
    <label>
      {label}
      <input
        type={inputType(field.type)}
        required={field.required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}

/** Maps a field type to the matching native <input> type. */
function inputType(type: FieldType): string {
  switch (type) {
    case FieldType.Email:
      return 'email';
    case FieldType.Phone:
      return 'tel';
    case FieldType.Url:
      return 'url';
    case FieldType.Number:
    case FieldType.Integer:
    case FieldType.Currency:
    case FieldType.Percent:
      return 'number';
    case FieldType.Date:
      return 'date';
    case FieldType.DateTime:
      return 'datetime-local';
    default:
      return 'text';
  }
}
